import { action, computed, observable } from 'mobx';
import { Stores } from '../models/Stores';
import { DataStore, FilterStore } from '.';
import { ShareClass, ShareClassDocument } from '../models/entity';
import {
    IFundCenterContext,
    IAnalytics,
} from '@kurtosys/ksys-app-template/dist/models/helpers/AnalyticsHelper';
import { DocumentCategory } from '../models/configuration/documents/DocumentCategory';
import { IQueryContext } from '@kurtosys/ksys-app-template/dist/models/query';
import getQueryResults from '../utils/query/getQueryResults';
import { RowConfigType } from '../models/configuration/explorer/RowConfigType';
import { RowConfigExpander } from '../models/configuration/explorer/RowConfigExpander';

export interface EntityGroupOptions {
    grouped: any;
    parentFundName?: string;
    fundName: string;
    isSubFund: boolean;
    subGroupedFund: any;
}

export class EntityGroupStore {
    @observable expanded = true;
    // Tracks all selected documents based on the entity
    @observable selectedDownloads: Map<
        string,
        Map<string, ShareClassDocument>
    > = new Map();
    // Track selected documents in the documents grid in the columns
    // DocumentMenuTitleControls will check the amount of documents selected
    // { entityKey: { columId: Documents } }
    @observable selectedGridDocuments: Map<
        string,
        Map<string, Set<ShareClassDocument>>
    > = new Map();
    @observable.ref entities: Array<ShareClass>;
    options: EntityGroupOptions;
    // Stores
    @observable.ref dataStore: DataStore;
    @observable.ref filterStore: FilterStore;

    constructor(
        stores: Stores,
        entities: Array<ShareClass>,
        options: EntityGroupOptions
    ) {
        this.dataStore = stores.data;
        this.filterStore = stores.filter;
        this.entities = entities;
        this.options = options;
    }

    @computed
    get shareClasses() {
        const shareClasses = this.entities.map(entity => {
            // If we have any property searches, filter the documents
            const documents = entity.documents.filter(document =>
                this.dataStore.documentPropertySearch.length > 0
                    ? this.filterStore.documentPropertyFilter(document)
                    : document
            );

            // Return shareClass with filtered documents
            return {
                ...entity,
                ...{
                    documents,
                },
                // Unfiltered original documents from the initial shareClass
                _originalDocuments: entity.documents,
            };
        });

        return shareClasses;
    }

    // Tracks all unique documents that have been selected
    @computed
    get allSelectedDownloads(): Array<ShareClassDocument> {
        const allDownloads = new Map<string, ShareClassDocument>();
        this.selectedDownloads.forEach((documents: Map<string, any>) => {
            documents.forEach((document: ShareClassDocument, key: string) => {
                allDownloads.set(key, document);
            });
        });
        return Array.from(allDownloads.values());
    }

    /**
     * @options passed to allow the calling computed to fire when filters change
     */
    includeDocumentByFilter = (
        entityDocument: ShareClassDocument,
        options: {
            documentFilterSearch: Array<any>;
            documentFilterInferredSearch: Array<any>;
            documentPropertySearch: Array<any>;
        }
    ) => {
        const { rowConfig } = this.dataStore.config;
        const { hideFilteredDocuments = false } = rowConfig;
        let includeDocument = true;
        if (hideFilteredDocuments) {
            if (options.documentPropertySearch.length > 0) {
                includeDocument =
                    this.filterStore.documentPropertyFilter(entityDocument) ||
                    false;
            }

            if (
                options.documentFilterSearch.length > 0 ||
                options.documentFilterInferredSearch.length > 0
            ) {
                includeDocument =
                    includeDocument &&
                    (this.filterStore.documentMetaOrInferredFilter(
                        entityDocument
                    ) ||
                        false);
            }
        }
        return includeDocument;
    };

    @action
    addSelectedDocument = (entityKey: string, documentEntity: any) => {
        const { clientCode: documentKey } = documentEntity;
        const documents = this.getSelectedDocumentsForEntity(entityKey);
        if (documents) {
            documents.set(documentKey, documentEntity);
        } else {
            // Set up new entity group and add document
            this.selectedDownloads.set(
                entityKey,
                new Map().set(documentKey, documentEntity)
            );
        }
    };

    @action
    clearSelectedDocuments = () => {
        this.selectedDownloads.clear();
    };

    @action
    getSelectedDocumentsForEntity = (entityKey: string) => {
        return this.selectedDownloads.get(entityKey);
    };

    @computed
    get rowHasExpandableOptions() {
        return Object.keys(this.dataStore.config.rowConfig).length > 0
            ? true
            : false;
    }

    @action
    toggleDownload = (
        documentEntity: any,
        entityKey: string,
        columnId?: string
    ) => {
        const { clientCode: documentKey } = documentEntity;
        const isDocumentsGrid = Boolean(columnId);

        const documents = this.getSelectedDocumentsForEntity(entityKey);
        if (documents && documents.has(documentKey)) {
            this.removeSelectedDocument(entityKey, documentKey);
        } else {
            this.addSelectedDocument(entityKey, documentEntity);
        }
        if (isDocumentsGrid) {
            this.toggleGridDocuments(documentEntity, entityKey, columnId);
        }
    };

    @action
    toggleGridDocuments = (
        documentEntity: any,
        entityKey: string,
        columnId: string
    ) => {
        const gridDocuments = this.selectedGridDocuments.get(entityKey);
        if (!gridDocuments) {
            this.selectedGridDocuments.set(
                entityKey,
                new Map().set(columnId, new Set().add(documentEntity))
            );
        } else if (!gridDocuments.has(columnId)) {
            gridDocuments.set(
                columnId,
                new Set<ShareClassDocument>().add(documentEntity)
            );
        } else {
            const documents = gridDocuments.get(columnId);
            if (documents.has(documentEntity)) {
                documents.delete(documentEntity);
            } else {
                documents.add(documentEntity);
            }
        }
    };

    @action
    removeSelectedDocument = (entityKey: string, documentKey: string) => {
        const documents = this.getSelectedDocumentsForEntity(entityKey);
        documents.delete(documentKey);
        // Remove empty entity group
        if (documents.size === 0) {
            this.selectedDownloads.delete(entityKey);
        }
    };

    @action
    toggleChildExpandStatus(override?: boolean) {
        const { shareClasses, dataStore } = this;
        // If the header is being collapsed then collapse child entities
        if (this.expanded) {
            for (const shareClass of shareClasses) {
                dataStore.toggleExpandStatus(
                    shareClass as any,
                    override !== undefined ? !override : this.expanded
                );
            }
        }
    }

    @action
    toggleExpanded = () => {
        const { dataStore, options } = this;
        this.toggleChildExpandStatus();
        this.expanded = !this.expanded;
        if (this.expanded) {
            const analyticsContext: IAnalytics<IFundCenterContext> = {
                event: 'table_row_expanded',
                eventType: 'userActioned',
                context: {
                    rowType: 'group',
                    rowData: { fundName: options.fundName },
                },
            };
            dataStore.analyticsHelper.logEvent(analyticsContext);
        }
    };

    @action
    setDocumentDownloads = () => {
        // Get array of all shareclass documents
        // If there are active document filters, filter documents by meta values
        const { dataStore, filterStore } = this;
        const {
            rowConfig,
            entityType,
            defaultDocumentSelectionState,
        } = dataStore.config;

        if (defaultDocumentSelectionState === 'selected') {
            const hasDocumentFilters =
                dataStore.documentFilterSearch.length > 0 ||
                dataStore.documentFilterInferredSearch.length > 0;
            for (const shareClass of this.shareClasses) {
                // filter out documents that are not showing due to categories not being configured.
                let result = [];
                if (this.hasDocumentCategories) {
                    const { documentCategories } = rowConfig;
                    for (const documentCategory of documentCategories as Array<
                        DocumentCategory
                    >) {
                        const queryContext: IQueryContext = {
                            entityByType: {
                                [entityType]: shareClass,
                            },
                        };
                        const documentCategoryQuery = {
                            documentGroup: documentCategory.groupBy,
                        };
                        const documentData = getQueryResults(
                            queryContext,
                            documentCategoryQuery,
                            dataStore.culture
                        );
                        result.push(...documentData.documentGroup);
                    }
                } else {
                    result = shareClass.documents;
                }
                // fall back to all shareclass documents if document filters are not configured.
                for (const document of result) {
                    if (
                        !hasDocumentFilters ||
                        filterStore.documentMetaOrInferredFilter(document)
                    ) {
                        this.addSelectedDocument(
                            shareClass.clientCode,
                            document
                        );
                    }
                }
            }
        }
    };

    @computed
    get hasDocumentCategories() {
        const { rowConfig } = this.dataStore.config;
        const { documentCategories } = rowConfig;
        return documentCategories && documentCategories.length > 0;
    }

    @computed
    get downloadCount() {
        return this.allSelectedDownloads.length;
    }

    @computed
    get rowType(): RowConfigType {
        const { type } = this.dataStore.config.rowConfig;
        return type;
    }

    @computed
    get rowExpanderConfig(): RowConfigExpander {
        const { expander } = this.dataStore.config.rowConfig;
        if (expander) {
            return {
                displayType: ['icon'],
                ...expander,
            };
        }
    }
}



// WEBPACK FOOTER //
// ./src/stores/EntityGroupStore.ts